import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type {
  BookingProposalDto,
  ConversationMessageDto,
  ConversationThreadDto,
} from "../types/conversations";

type LoadState = "idle" | "loading" | "loaded" | "error";
type SubmitState = "idle" | "submitting" | "submitted" | "error";
type ProposalLoadState = "idle" | "loading" | "loaded" | "error";
type RecentSendKind = "reply" | "rebook" | null;

const OWNER_POSTCODE_STORAGE_KEY = "wnm.ownerPostcode";

function formatDateTime(value?: string | null): string {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value?: string | null): string {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatMoney(amountMinorUnits: number, currency?: string | null): string {
  const safeCurrency = (currency || "GBP").toUpperCase();

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: safeCurrency,
  }).format((amountMinorUnits || 0) / 100);
}

function buildWalkerName(thread: ConversationThreadDto | null): string {
  if (!thread) return "walker";

  const full = [thread.walkerFirstName, thread.walkerLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return full || thread.walkerDisplayTitle || "walker";
}

function buildRecipientIntro(thread: ConversationThreadDto | null): string {
  if (!thread) return "Conversation";

  if (thread.tokenRecipientType === "Walker") {
    const ownerName = thread.ownerName?.trim();
    if (ownerName) return `Reply to ${ownerName}`;
    return "Reply to owner";
  }

  return `Reply to ${buildWalkerName(thread)}`;
}

function buildSenderLabel(
  msg: ConversationMessageDto,
  thread: ConversationThreadDto | null
): string {
  if (msg.senderType === "Walker") {
    return buildWalkerName(thread);
  }

  const ownerName = thread?.ownerName?.trim();
  return ownerName || "Owner";
}

function normalizeProposalStatus(status?: string | null): string {
  return (status || "").trim().toLowerCase();
}

function isProposalPending(proposal: BookingProposalDto): boolean {
  return (
    normalizeProposalStatus(proposal.status) === "sent" ||
    normalizeProposalStatus(proposal.status) === "checkoutcreated"
  );
}

function isProposalPaid(proposal: BookingProposalDto): boolean {
  return normalizeProposalStatus(proposal.status) === "paid";
}

function isProposalCompleted(proposal: BookingProposalDto): boolean {
  return normalizeProposalStatus(proposal.status) === "completed";
}

function isProposalCancelled(proposal: BookingProposalDto): boolean {
  return normalizeProposalStatus(proposal.status) === "cancelled";
}

function getProposalStatusLabel(proposal: BookingProposalDto): string {
  if (isProposalPaid(proposal)) return "Paid";
  if (isProposalCompleted(proposal)) return "Completed";
  if (isProposalCancelled(proposal)) return "Cancelled";
  if (normalizeProposalStatus(proposal.status) === "checkoutcreated") {
    return "Payment pending";
  }
  return proposal.status?.trim() || "Sent";
}

function buildRebookMessage(
  walkerName: string,
  proposal: BookingProposalDto
): string {
  const serviceType = proposal.serviceType?.trim() || "walk";
  const hasDogs = typeof proposal.dogCount === "number" && proposal.dogCount > 0;
  const dogText = hasDogs
    ? ` for ${proposal.dogCount} dog${proposal.dogCount === 1 ? "" : "s"}`
    : "";
  const locationText = proposal.locationSummary?.trim()
    ? ` near ${proposal.locationSummary.trim()}`
    : "";

  return `Hi ${walkerName}, I'd like to book another ${serviceType.toLowerCase()}${dogText}${locationText}. Are you available?`;
}

function readStoredOwnerPostcode(): string {
  try {
    return localStorage.getItem(OWNER_POSTCODE_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

const ConversationReplyPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagePanelRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const hasAppliedRebookRef = useRef(false);
  const highlightTimeoutRef = useRef<number | null>(null);

  const [thread, setThread] = useState<ConversationThreadDto | null>(null);
  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const [proposals, setProposals] = useState<BookingProposalDto[]>([]);
  const [proposalState, setProposalState] = useState<ProposalLoadState>("idle");
  const [proposalError, setProposalError] = useState("");
  const [proposalBusyId, setProposalBusyId] = useState<number | null>(null);
  const [rebookBusyId, setRebookBusyId] = useState<number | null>(null);
  const [sentRebookProposalId, setSentRebookProposalId] = useState<number | null>(null);
  const [recentSendKind, setRecentSendKind] = useState<RecentSendKind>(null);
  const [recentSentMessageId, setRecentSentMessageId] = useState<number | null>(null);
  const [highlightMessageId, setHighlightMessageId] = useState<number | null>(null);

  const pageTitle = useMemo(() => buildRecipientIntro(thread), [thread]);
  const walkerName = useMemo(() => buildWalkerName(thread), [thread]);
  const viewerIsOwner = thread?.tokenRecipientType === "Owner";

  const paymentStatus = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return (qs.get("payment") || "").trim().toLowerCase();
  }, [location.search]);

  const preferredPostcode = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromQuery = (qs.get("postcode") || "").trim();
    return fromQuery || readStoredOwnerPostcode();
  }, [location.search]);

  const rebookProposalId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const raw = (qs.get("rebook") || "").trim();
    if (!raw) return 0;

    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [location.search]);

  function scrollMessagesToBottom(behavior: ScrollBehavior = "smooth") {
    messagesEndRef.current?.scrollIntoView({
      behavior,
      block: "end",
    });
  }

  function focusLatestMessages(behavior: ScrollBehavior = "smooth") {
    messagePanelRef.current?.scrollIntoView({
      behavior,
      block: "start",
    });
  }

  function getLatestMessageId(conversation: ConversationThreadDto | null): number | null {
    if (!conversation?.messages?.length) return null;
    const latest = conversation.messages[conversation.messages.length - 1];
    return typeof latest.messageId === "number" ? latest.messageId : null;
  }

  function setTemporaryHighlight(messageId: number | null) {
    if (highlightTimeoutRef.current !== null) {
      window.clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }

    setHighlightMessageId(messageId);

    if (messageId !== null) {
      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightMessageId(null);
        highlightTimeoutRef.current = null;
      }, 10000);
    }
  }

  async function loadConversationProposalsByToken(replyToken: string) {
    try {
      setProposalState("loading");
      setProposalError("");

      const result =
        await walkiesClient.getConversationBookingProposalsByToken(replyToken);

      setProposals(result);
      setProposalState("loaded");
    } catch {
      setProposals([]);
      setProposalState("error");
      setProposalError(
        "Booking proposals are not available from this reply link yet."
      );
    }
  }

  useEffect(() => {
    hasAppliedRebookRef.current = false;
  }, [location.search]);

  useEffect(() => {
    async function load() {
      if (!token?.trim()) {
        setState("error");
        setError("Reply link is missing or invalid.");
        return;
      }

      try {
        setState("loading");
        setError("");
        setProposalError("");

        const data = await walkiesClient.getConversationByToken(token);
        setThread(data);
        setState("loaded");

        await loadConversationProposalsByToken(token);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to load this conversation.";

        setThread(null);
        setProposals([]);
        setState("error");
        setError(message);
      }
    }

    void load();
  }, [token]);

  useEffect(() => {
    if (!viewerIsOwner) return;
    if (!rebookProposalId) return;
    if (hasAppliedRebookRef.current) return;
    if (!proposals.length) return;

    const proposal = proposals.find(
      (p) => p.bookingProposalId === rebookProposalId
    );

    if (!proposal) return;

    const nextMessage = buildRebookMessage(walkerName, proposal);

    setMessage(nextMessage);
    setSubmitState("idle");
    setSubmitError("");
    setSubmitSuccess("");
    hasAppliedRebookRef.current = true;

    window.setTimeout(() => {
      replyTextareaRef.current?.focus();
      replyTextareaRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);
  }, [viewerIsOwner, rebookProposalId, proposals, walkerName]);

  useEffect(() => {
    if (!thread?.messages?.length) return;

    const timeout = window.setTimeout(() => {
      scrollMessagesToBottom("auto");
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [thread?.messages.length]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current !== null) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  async function handleReplySubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmedToken = token?.trim() ?? "";
    const trimmedMessage = message.trim();

    if (!trimmedToken) {
      setSubmitState("error");
      setSubmitError("Reply token is missing.");
      return;
    }

    if (!trimmedMessage) {
      setSubmitState("error");
      setSubmitError("Please enter a message.");
      return;
    }

    try {
      setSubmitState("submitting");
      setSubmitError("");
      setSubmitSuccess("");
      setSentRebookProposalId(null);

      let updatedConversation: ConversationThreadDto | null = null;

      const result = await walkiesClient.replyToConversation({
        token: trimmedToken,
        message: trimmedMessage,
      });

      if (result.conversation) {
        updatedConversation = result.conversation;
        setThread(updatedConversation);
        await loadConversationProposalsByToken(trimmedToken);
      } else {
        try {
          const reloaded = await walkiesClient.getConversationByToken(trimmedToken);
          updatedConversation = reloaded;
          setThread(reloaded);
          await loadConversationProposalsByToken(trimmedToken);
        } catch {
          // ignore reload failure after successful send
        }
      }

      const latestId = getLatestMessageId(updatedConversation);

      setMessage("");
      setSubmitState("submitted");
      setRecentSendKind("reply");
      setRecentSentMessageId(latestId);
      setTemporaryHighlight(latestId);
      setSubmitSuccess(
        result.notifiedRecipientType === "Walker"
          ? "Your reply has been sent to the walker."
          : "Your reply has been sent to the owner."
      );

      window.setTimeout(() => {
        focusLatestMessages("smooth");
        window.setTimeout(() => {
          scrollMessagesToBottom("smooth");
        }, 180);
      }, 60);
    } catch (err) {
      const nextMessage =
        err instanceof Error ? err.message : "Failed to send reply.";
      setSubmitState("error");
      setSubmitError(nextMessage);
    }
  }

  async function handlePayProposal(proposal: BookingProposalDto) {
    const trimmedToken = token?.trim() ?? "";

    if (!trimmedToken) {
      setProposalError("Reply token is missing.");
      return;
    }

    try {
      setProposalBusyId(proposal.bookingProposalId);
      setProposalError("");

      const successParams = new URLSearchParams();
      successParams.set("payment", "success");
      if (preferredPostcode) {
        successParams.set("postcode", preferredPostcode);
      }

      const cancelParams = new URLSearchParams();
      cancelParams.set("payment", "cancelled");
      if (preferredPostcode) {
        cancelParams.set("postcode", preferredPostcode);
      }

      const successUrl = `${window.location.origin}/reply/${encodeURIComponent(
        trimmedToken
      )}?${successParams.toString()}`;

      const cancelUrl = `${window.location.origin}/reply/${encodeURIComponent(
        trimmedToken
      )}?${cancelParams.toString()}`;

      const result =
        await walkiesClient.createBookingProposalCheckoutSessionByToken(
          proposal.bookingProposalId,
          {
            token: trimmedToken,
            successUrl,
            cancelUrl,
          }
        );

      const redirectUrl = result.checkoutUrl || "";

      if (!redirectUrl) {
        throw new Error("Stripe checkout URL was not returned.");
      }

      window.location.href = redirectUrl;
    } catch (err) {
      setProposalError(
        err instanceof Error
          ? err.message
          : "Failed to start secure payment."
      );
    } finally {
      setProposalBusyId(null);
    }
  }

  async function handleBookAgain(proposal: BookingProposalDto) {
    const trimmedToken = token?.trim() ?? "";

    if (!trimmedToken) {
      setSubmitState("error");
      setSubmitError("Reply token is missing.");
      return;
    }

    try {
      const nextMessage = buildRebookMessage(walkerName, proposal);

      setRebookBusyId(proposal.bookingProposalId);
      setSubmitState("submitting");
      setSubmitError("");
      setSubmitSuccess("");

      await walkiesClient.createConversationRebookRequest({
        token: trimmedToken,
        message: nextMessage,
      });

      let updatedConversation: ConversationThreadDto | null = null;

      try {
        const reloaded = await walkiesClient.getConversationByToken(trimmedToken);
        updatedConversation = reloaded;
        setThread(reloaded);
        await loadConversationProposalsByToken(trimmedToken);
      } catch {
        // ignore reload failure after successful rebook creation
      }

      const latestId = getLatestMessageId(updatedConversation);

      setMessage("");
      setSubmitState("submitted");
      setSentRebookProposalId(proposal.bookingProposalId);
      setRecentSendKind("rebook");
      setRecentSentMessageId(latestId);
      setTemporaryHighlight(latestId);
      setSubmitSuccess(
        "Booking request sent. The walker can now reply in this conversation."
      );

      window.setTimeout(() => {
        focusLatestMessages("smooth");
        window.setTimeout(() => {
          scrollMessagesToBottom("smooth");
        }, 180);
      }, 60);
    } catch (err) {
      const nextMessage =
        err instanceof Error ? err.message : "Failed to create booking request.";
      setSubmitState("error");
      setSubmitError(nextMessage);
    } finally {
      setRebookBusyId(null);
    }
  }

  function handleFindAnotherWalker() {
    const qs = new URLSearchParams();

    if (preferredPostcode) {
      qs.set("postcode", preferredPostcode);
    }

    qs.set("from", "rebook");

    if (walkerName) {
      qs.set("previousWalker", walkerName);
    }

    navigate(`/walkers?${qs.toString()}`);
  }

  if (state === "loading" || state === "idle") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loadingCard}>Loading conversation...</div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.errorCard}>
            <div style={styles.badgeRow}>
              <span style={styles.badge}>Secure reply</span>
            </div>

            <h1 style={styles.errorTitle}>This reply link is not available</h1>
            <p style={styles.errorText}>
              {error ||
                "This conversation link may have expired, already been used, or no longer be available."}
            </p>

            <div style={styles.errorBox}>
              For security, WalkiesNearMe conversation links can expire. If you’re
              the owner, you can request fresh secure access links using the email
              address you used when you first contacted the walker.
            </div>

            <div style={styles.helpCard}>
              <h2 style={styles.helpCardTitle}>
                Need to get back into your conversation?
              </h2>
              <p style={styles.helpCardText}>
                Use owner conversation recovery and we’ll email you fresh secure
                links for any recent conversations we find for your email address.
              </p>
              <div style={styles.helpActions}>
                <Link to="/reply-access" style={styles.primaryLinkButton}>
                  Request fresh access links
                </Link>
                <Link to="/walkers" style={styles.secondaryLinkButton}>
                  Browse walkers
                </Link>
                <Link to="/" style={styles.secondaryLinkButton}>
                  Back to home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.errorCard}>
            <h1 style={styles.errorTitle}>Conversation not found</h1>
            <p style={styles.errorText}>
              We could not load this conversation thread.
            </p>
            <Link to="/" style={styles.primaryLinkButton}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <section style={styles.heroCard}>
          <div style={styles.badgeRow}>
            <span style={styles.badge}>Secure reply</span>
            <span style={styles.badgeSoft}>
              Link expires {formatDateTime(thread.tokenExpiresUtc)}
            </span>
          </div>

          <h1 style={styles.title}>{pageTitle}</h1>

          <p style={styles.subtitle}>
            You are viewing a secure WalkiesNearMe conversation thread.
          </p>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Walker</div>
              <div style={styles.summaryValue}>{walkerName}</div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Conversation started</div>
              <div style={styles.summaryValue}>
                {formatDateTime(thread.createdUtc) || "—"}
              </div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Last message</div>
              <div style={styles.summaryValue}>
                {formatDateTime(thread.lastMessageUtc) || "—"}
              </div>
            </div>
          </div>
        </section>

        {paymentStatus === "success" && (
          <div style={styles.globalSuccessBanner}>
            Payment completed successfully. The walker will be notified.
          </div>
        )}

        {paymentStatus === "cancelled" && (
          <div style={styles.globalWarnBanner}>
            Payment was cancelled before checkout completed.
          </div>
        )}

        <div style={styles.layoutGrid}>
          <section style={styles.threadCard}>
            <div style={styles.threadHeader}>
              <h2 style={styles.sectionTitle}>Conversation</h2>
              <div style={styles.threadCount}>
                {thread.messages.length} message
                {thread.messages.length === 1 ? "" : "s"}
              </div>
            </div>

            {thread.statusId === 2 && (
              <div style={styles.closedBanner}>
                <div style={styles.closedBannerText}>
                  This conversation is archived. Sending a reply will reopen it
                  automatically.
                </div>
              </div>
            )}

            <section style={styles.proposalsSection}>
              <div style={styles.proposalsHeader}>
                <div>
                  <h3 style={styles.proposalsTitle}>Booking proposals</h3>
                  <div style={styles.proposalIntro}>
                    If the walker has sent you a booking proposal, you can review
                    it and pay securely here.
                  </div>
                </div>

                <div style={styles.threadCount}>
                  {proposals.length} proposal{proposals.length === 1 ? "" : "s"}
                </div>
              </div>

              {proposalError && (
                <div style={styles.proposalErrorBox}>{proposalError}</div>
              )}

              {proposalState === "loading" ? (
                <div style={styles.emptyProposalBox}>
                  Loading booking proposals...
                </div>
              ) : proposals.length === 0 ? (
                <div style={styles.emptyProposalBox}>
                  No booking proposals have been shared in this conversation yet.
                </div>
              ) : (
                <div style={styles.proposalList}>
                  {proposals.map((proposal) => {
                    const canPay = viewerIsOwner && isProposalPending(proposal);
                    const showRepeatActions =
                      viewerIsOwner &&
                      (isProposalPaid(proposal) || isProposalCompleted(proposal));
                    const isSendingRebook =
                      rebookBusyId === proposal.bookingProposalId;
                    const isRebookSent =
                      sentRebookProposalId === proposal.bookingProposalId;

                    return (
                      <div key={proposal.bookingProposalId} style={styles.proposalCard}>
                        <div style={styles.proposalTopRow}>
                          <div>
                            <div style={styles.proposalTitle}>
                              {proposal.serviceType}
                            </div>
                            <div style={styles.proposalDate}>
                              {formatDate(proposal.serviceDateLocal) ||
                                "Date to be confirmed"}
                            </div>
                          </div>

                          <span
                            style={{
                              ...styles.proposalStatusBadge,
                              ...(isProposalPaid(proposal)
                                ? styles.proposalStatusPaid
                                : isProposalCompleted(proposal)
                                ? styles.proposalStatusCompleted
                                : isProposalCancelled(proposal)
                                ? styles.proposalStatusCancelled
                                : styles.proposalStatusPending),
                            }}
                          >
                            {getProposalStatusLabel(proposal)}
                          </span>
                        </div>

                        <div style={styles.proposalAmount}>
                          {formatMoney(
                            proposal.grossAmountPence,
                            proposal.currency
                          )}
                        </div>

                        {(proposal.locationSummary?.trim() || proposal.notes?.trim()) && (
                          <div style={styles.proposalDescription}>
                            {proposal.locationSummary?.trim() && (
                              <div>
                                <strong>Location:</strong> {proposal.locationSummary}
                              </div>
                            )}
                            {proposal.notes?.trim() && (
                              <div>
                                <strong>Notes:</strong> {proposal.notes}
                              </div>
                            )}
                          </div>
                        )}

                        <div style={styles.proposalMetaRow}>
                          <span>Created {formatDateTime(proposal.createdUtc)}</span>

                          {proposal.paidUtc && (
                            <span>Paid {formatDateTime(proposal.paidUtc)}</span>
                          )}

                          {proposal.completedUtc && (
                            <span>
                              Completed {formatDateTime(proposal.completedUtc)}
                            </span>
                          )}

                          {proposal.cancelledUtc && (
                            <span>
                              Cancelled {formatDateTime(proposal.cancelledUtc)}
                            </span>
                          )}
                        </div>

                        <div style={styles.proposalActionRow}>
                          {canPay ? (
                            <>
                              <div style={styles.proposalHelpText}>
                                Ready to book? Pay securely through Stripe.
                              </div>

                              <button
                                type="button"
                                style={styles.payButton}
                                disabled={proposalBusyId === proposal.bookingProposalId}
                                onClick={() => handlePayProposal(proposal)}
                              >
                                {proposalBusyId === proposal.bookingProposalId
                                  ? "Redirecting..."
                                  : "Pay Securely"}
                              </button>
                            </>
                          ) : isProposalPaid(proposal) ? (
                            <div style={styles.proposalPaidText}>
                              Payment received successfully.
                            </div>
                          ) : isProposalCompleted(proposal) ? (
                            <div style={styles.proposalCompletedText}>
                              This booking has been marked completed.
                            </div>
                          ) : isProposalCancelled(proposal) ? (
                            <div style={styles.proposalCancelledText}>
                              This proposal has been cancelled.
                            </div>
                          ) : (
                            <div style={styles.proposalHelpText}>
                              Waiting for the next step on this proposal.
                            </div>
                          )}
                        </div>

                        {showRepeatActions && (
                          <div style={styles.repeatActionRow}>
                            <button
                              type="button"
                              style={{
                                ...styles.repeatPrimaryButton,
                                ...(isRebookSent ? styles.repeatPrimaryButtonSent : {}),
                              }}
                              onClick={() => handleBookAgain(proposal)}
                              disabled={isSendingRebook || isRebookSent}
                            >
                              {isSendingRebook
                                ? "Sending request..."
                                : isRebookSent
                                ? "Request sent"
                                : "Request another booking"}
                            </button>

                            <button
                              type="button"
                              style={styles.repeatSecondaryButton}
                              onClick={handleFindAnotherWalker}
                            >
                              Find another walker
                            </button>
                          </div>
                        )}

                        {isRebookSent && (
                          <div style={styles.inlineSentNote}>
                            Booking request sent. The walker can now reply in this conversation.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section ref={messagePanelRef} style={styles.messagesSection}>
              <div style={styles.messagesHeaderRow}>
                <h3 style={styles.messagesTitle}>Latest messages</h3>
                <div style={styles.messagesHint}>Newest message stays in view</div>
              </div>

              <div style={styles.messageListScroll}>
                <div style={styles.messageList}>
                  {thread.messages.map((msg, index) => {
                    const isCurrentViewer = msg.senderType === thread.tokenRecipientType;
                    const senderLabel = buildSenderLabel(msg, thread);
                    const isLatest = index === thread.messages.length - 1;
                    const showRecentMarker =
                      isLatest &&
                      recentSentMessageId !== null &&
                      recentSentMessageId === msg.messageId &&
                      recentSendKind !== null;
                    const isHighlighted =
                      highlightMessageId !== null &&
                      highlightMessageId === msg.messageId;

                    return (
                      <React.Fragment key={msg.messageId}>
                        {showRecentMarker ? (
                          <div style={styles.recentSendMarker}>
                            {recentSendKind === "rebook"
                              ? "Booking request sent just now"
                              : "Reply sent just now"}
                          </div>
                        ) : null}

                        <div
                          style={{
                            ...styles.messageBubble,
                            ...(isCurrentViewer
                              ? styles.messageBubbleMine
                              : styles.messageBubbleOther),
                            ...(isHighlighted ? styles.messageBubbleHighlight : {}),
                          }}
                        >
                          <div style={styles.messageMeta}>
                            <span style={styles.messageSender}>{senderLabel}</span>
                            <span style={styles.messageDate}>
                              {formatDateTime(msg.createdUtc)}
                            </span>
                          </div>

                          <div style={styles.messageText}>{msg.messageText}</div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </section>
          </section>

          <aside style={styles.replyCard}>
            <h2 style={styles.sectionTitle}>Send a reply</h2>
            <p style={styles.replyIntro}>
              Your message will be emailed to the other person with a fresh secure
              reply link.
            </p>

            {viewerIsOwner && (
              <div style={styles.ownerHelpBox}>
                If you lose access later, you can always request fresh owner
                conversation links by email.
                <div style={styles.ownerHelpActions}>
                  <Link to="/reply-access" style={styles.ownerHelpLink}>
                    Owner conversation access
                  </Link>

                  {token?.trim() && (
                    <Link
                      to={`/owner/bookings/${encodeURIComponent(token.trim())}`}
                      style={styles.ownerHelpLinkAlt}
                    >
                      View all your bookings
                    </Link>
                  )}
                </div>
              </div>
            )}

            {submitState === "submitted" && submitSuccess && (
              <div style={styles.successBox}>{submitSuccess}</div>
            )}

            {submitState === "error" && submitError && (
              <div style={styles.submitErrorBox}>{submitError}</div>
            )}

            <form onSubmit={handleReplySubmit} style={styles.form}>
              <div style={styles.field}>
                <label htmlFor="replyMessage" style={styles.label}>
                  Message
                </label>
                <textarea
                  id="replyMessage"
                  ref={replyTextareaRef}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);

                    if (submitState !== "idle") {
                      setSubmitState("idle");
                    }

                    if (submitError) {
                      setSubmitError("");
                    }

                    if (submitSuccess) {
                      setSubmitSuccess("");
                    }
                  }}
                  style={styles.textarea}
                  placeholder="Write your reply here..."
                  rows={8}
                  required
                />
              </div>

              <div style={styles.formActions}>
                <button
                  type="submit"
                  style={styles.submitButton}
                  disabled={submitState === "submitting"}
                >
                  {submitState === "submitting" ? "Sending..." : "Send reply"}
                </button>
              </div>
            </form>
          </aside>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "24px 16px 56px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 1080,
    margin: "0 auto",
  },
  heroCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    marginBottom: 22,
  },
  badgeRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  badgeSoft: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    background: "rgba(242, 138, 22, 0.18)",
    border: "1px solid rgba(242, 138, 22, 0.28)",
    color: "#fff7ef",
  },
  title: {
    margin: "0 0 10px",
    fontSize: 40,
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
  },
  subtitle: {
    margin: "0 0 20px",
    fontSize: 16,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.82)",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  summaryCard: {
    background: "rgba(255,255,255,0.08)",
    borderRadius: 20,
    padding: 16,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  summaryLabel: {
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "rgba(255,255,255,0.68)",
    marginBottom: 8,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 800,
    color: "#ffffff",
  },
  globalSuccessBanner: {
    borderRadius: 18,
    padding: 16,
    marginBottom: 20,
    background: "rgba(33, 58, 51, 0.16)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#ffffff",
    fontWeight: 800,
  },
  globalWarnBanner: {
    borderRadius: 18,
    padding: 16,
    marginBottom: 20,
    background: "rgba(242, 138, 22, 0.16)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#ffffff",
    fontWeight: 800,
  },
  layoutGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 360px)",
    gap: 20,
    alignItems: "start",
  },
  threadCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  replyCard: {
    background: "linear-gradient(180deg, #f28a16 0%, #da760b 100%)",
    color: "#ffffff",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    alignSelf: "start",
    position: "sticky",
    top: 16,
  },
  threadHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  threadCount: {
    fontSize: 14,
    fontWeight: 800,
    color: "rgba(24, 32, 29, 0.58)",
  },
  sectionTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
  },
  closedBanner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: 14,
    borderRadius: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.16)",
    marginBottom: 18,
  },
  closedBannerText: {
    fontWeight: 700,
    color: "#7f1d1d",
  },
  proposalsSection: {
    display: "grid",
    gap: 16,
    marginBottom: 24,
    paddingBottom: 24,
    borderBottom: "1px solid rgba(24, 32, 29, 0.08)",
  },
  proposalsHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  proposalsTitle: {
    margin: 0,
    fontSize: 24,
    lineHeight: 1.1,
    color: "#213a33",
  },
  proposalIntro: {
    marginTop: 8,
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.7,
  },
  emptyProposalBox: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(24, 32, 29, 0.05)",
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.7,
  },
  proposalErrorBox: {
    borderRadius: 16,
    padding: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.14)",
    color: "#7f1d1d",
    fontWeight: 700,
  },
  proposalList: {
    display: "grid",
    gap: 14,
  },
  proposalCard: {
    borderRadius: 18,
    padding: 18,
    background: "#ffffff",
    border: "1px solid rgba(24, 32, 29, 0.08)",
    display: "grid",
    gap: 12,
  },
  proposalTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  proposalTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: "#213a33",
    lineHeight: 1.2,
  },
  proposalDate: {
    marginTop: 6,
    color: "rgba(24, 32, 29, 0.66)",
    fontWeight: 700,
  },
  proposalAmount: {
    fontSize: 28,
    lineHeight: 1,
    fontWeight: 900,
    color: "#f28a16",
  },
  proposalDescription: {
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.84)",
    whiteSpace: "pre-wrap",
    display: "grid",
    gap: 6,
  },
  proposalMetaRow: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    color: "rgba(24, 32, 29, 0.6)",
    fontSize: 13,
  },
  proposalStatusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 13,
  },
  proposalStatusPending: {
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#b45309",
  },
  proposalStatusPaid: {
    background: "rgba(21, 128, 61, 0.12)",
    border: "1px solid rgba(21, 128, 61, 0.22)",
    color: "#166534",
  },
  proposalStatusCompleted: {
    background: "rgba(33, 58, 51, 0.12)",
    border: "1px solid rgba(33, 58, 51, 0.18)",
    color: "#213a33",
  },
  proposalStatusCancelled: {
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.16)",
    color: "#7f1d1d",
  },
  proposalActionRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  proposalHelpText: {
    fontWeight: 700,
    color: "rgba(24, 32, 29, 0.72)",
  },
  proposalPaidText: {
    fontWeight: 700,
    color: "#166534",
  },
  proposalCompletedText: {
    fontWeight: 700,
    color: "#213a33",
  },
  proposalCancelledText: {
    fontWeight: 700,
    color: "#7f1d1d",
  },
  payButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#f28a16",
    color: "#ffffff",
  },
  repeatActionRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    paddingTop: 4,
  },
  repeatPrimaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 14,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    background: "#213a33",
    color: "#ffffff",
  },
  repeatPrimaryButtonSent: {
    background: "#2f4f46",
    opacity: 0.88,
    cursor: "default",
  },
  repeatSecondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 14,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    background: "#f5f0e8",
    color: "#213a33",
    border: "1px solid rgba(24, 32, 29, 0.16)",
  },
  inlineSentNote: {
    borderRadius: 14,
    padding: "12px 14px",
    background: "rgba(33, 58, 51, 0.06)",
    border: "1px solid rgba(33, 58, 51, 0.12)",
    color: "#213a33",
    fontWeight: 700,
    lineHeight: 1.6,
  },
  messagesSection: {
    display: "grid",
    gap: 12,
  },
  messagesHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  messagesTitle: {
    margin: 0,
    fontSize: 24,
    lineHeight: 1.1,
    color: "#213a33",
  },
  messagesHint: {
    fontSize: 13,
    fontWeight: 800,
    color: "rgba(24, 32, 29, 0.56)",
  },
  messageListScroll: {
    maxHeight: 520,
    overflowY: "auto",
    paddingRight: 6,
    borderRadius: 18,
    background: "rgba(24, 32, 29, 0.03)",
    border: "1px solid rgba(24, 32, 29, 0.08)",
    padding: 14,
  },
  messageList: {
    display: "grid",
    gap: 14,
  },
  recentSendMarker: {
    justifySelf: "center",
    borderRadius: 999,
    padding: "8px 12px",
    background: "rgba(33, 58, 51, 0.08)",
    border: "1px solid rgba(33, 58, 51, 0.14)",
    color: "#213a33",
    fontWeight: 800,
    fontSize: 13,
  },
  messageBubble: {
    borderRadius: 18,
    padding: 16,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  messageBubbleMine: {
    background: "rgba(47, 79, 70, 0.08)",
    border: "1px solid rgba(47, 79, 70, 0.12)",
  },
  messageBubbleOther: {
    background: "rgba(242, 138, 22, 0.08)",
    border: "1px solid rgba(242, 138, 22, 0.16)",
  },
  messageBubbleHighlight: {
    border: "2px solid #f28a16",
    boxShadow:
      "0 0 0 3px rgba(242,138,22,0.22), 0 0 24px rgba(242,138,22,0.18)",
    animation: "wnmMessagePulse 1.2s ease-in-out 2",
    transition: "box-shadow 0.35s ease, border 0.35s ease",
  },
  messageMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  messageSender: {
    fontWeight: 800,
    color: "#213a33",
  },
  messageDate: {
    fontSize: 13,
    fontWeight: 700,
    color: "rgba(24, 32, 29, 0.56)",
  },
  messageText: {
    lineHeight: 1.75,
    color: "rgba(24, 32, 29, 0.88)",
  },
  replyIntro: {
    margin: "10px 0 18px",
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.92)",
  },
  ownerHelpBox: {
    borderRadius: 16,
    padding: 14,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.24)",
    color: "#ffffff",
    lineHeight: 1.7,
    marginBottom: 16,
    fontWeight: 700,
  },
  ownerHelpActions: {
    marginTop: 10,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  ownerHelpLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 14,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 800,
    background: "#213a33",
    color: "#ffffff",
    textDecoration: "none",
  },
  ownerHelpLinkAlt: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 14,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 800,
    background: "rgba(255,255,255,0.16)",
    color: "#ffffff",
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.24)",
  },
  successBox: {
    background: "rgba(212, 255, 217, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(212, 255, 217, 0.38)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  submitErrorBox: {
    background: "rgba(155, 28, 28, 0.18)",
    color: "#ffffff",
    border: "1px solid rgba(255, 219, 219, 0.34)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 16,
    fontWeight: 700,
  },
  form: {
    display: "grid",
    gap: 16,
  },
  field: {
    display: "grid",
    gap: 8,
  },
  label: {
    fontWeight: 800,
    fontSize: 14,
    color: "#ffffff",
  },
  textarea: {
    width: "100%",
    font: "inherit",
    padding: "14px 14px",
    borderRadius: 14,
    outline: "none",
    border: "1px solid rgba(255,255,255,0.24)",
    background: "rgba(255,255,255,0.12)",
    color: "#ffffff",
    resize: "vertical",
    minHeight: 160,
  },
  formActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  submitButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#213a33",
    color: "#ffffff",
  },
  loadingCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#ffffff",
    borderRadius: 24,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    fontSize: 18,
    fontWeight: 700,
  },
  errorCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 24,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  errorTitle: {
    margin: "0 0 10px",
    fontSize: 28,
    lineHeight: 1.08,
    color: "#213a33",
  },
  errorText: {
    margin: "0 0 18px",
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.8)",
  },
  errorBox: {
    background: "rgba(24, 32, 29, 0.05)",
    border: "1px solid rgba(24, 32, 29, 0.08)",
    color: "rgba(24, 32, 29, 0.82)",
    borderRadius: 18,
    padding: 16,
    marginBottom: 18,
    lineHeight: 1.7,
  },
  helpCard: {
    borderRadius: 20,
    padding: 18,
    background: "rgba(242, 138, 22, 0.08)",
    border: "1px solid rgba(242, 138, 22, 0.16)",
  },
  helpCardTitle: {
    margin: "0 0 8px",
    fontSize: 22,
    lineHeight: 1.1,
    color: "#213a33",
  },
  helpCardText: {
    margin: "0 0 14px",
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.82)",
  },
  helpActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#f28a16",
    color: "#ffffff",
    textDecoration: "none",
  },
  secondaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#213a33",
    color: "#ffffff",
    textDecoration: "none",
  },
};

export default ConversationReplyPage;